/**
 * AGED CARE: WHAT THE FAMILY RECEIVES FROM A PLAN'S GUARANTEED INCOME (guaranteed income review E4/M4, 21 Oct
 * 2026; the agreed mock-up docs/ux-review/annuities/ screens 3 and 4).
 *
 * The shared table (AnnuityFamilyPanel) on every aged care plan with a guaranteed income: the tested plan cards,
 * the chosen plan when the client comes back, and the hand-built plan. A tested plan is compared with THE SAME
 * DEPOSIT WITHOUT THE LEVER: agedCareMeansTestCandidates layers each lever onto "Do Nothing" and the first
 * "Pay 50/75/100% RAD…" of each level and names the candidate `${base.name} + …`, so the base is the evaluated
 * row whose name is everything before " + ". The hand-built plan has no such twin, so it shows no comparison.
 *
 * Also here: which engine rows are income only (E5), so the panel can hold "Choose this plan" until the client
 * has confirmed it.
 */
import React from 'react';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { formatCurrency } from '../../utils/index.ts';
import AnnuityFamilyPanel, { familyPanelKindOf, type FamilyPanelKind } from '../../components/strategy/AnnuityFamilyPanel.tsx';
import { isIncomeOnlyAnnuity } from '../../components/strategy/IncomeOnlyTick.tsx';

interface EngineRowLike {
    name?: string;
    meansTestStrategy?: { type?: string; purchaseAmount?: number; annuityType?: string; residualCapital?: number; indexation?: boolean; [key: string]: unknown } | null;
    annuityTerms?: { purchaseAmount?: unknown; annualPayment?: unknown; [key: string]: unknown } | null;
    projections?: Record<string, unknown>[];
    [key: string]: unknown;
}

export interface EngineAnnuityLever {
    amount: number;
    kind: FamilyPanelKind;
    incomeOnly: boolean;
    incomeAYear: number | null;
}

/** The guaranteed income an engine row buys, or null when it buys none. */
export function engineAnnuityLeverOf(row: EngineRowLike | null | undefined): EngineAnnuityLever | null {
    const lever = row?.meansTestStrategy;
    if (!lever || lever.type !== 'annuity') return null;
    // The amount actually bought (annuityTerms reads the built product), else the amount asked for.
    const amount = Number(row?.annuityTerms?.purchaseAmount) || Number(lever.purchaseAmount) || 0;
    if (!(amount > 0)) return null;
    return {
        amount,
        kind: familyPanelKindOf(lever),
        incomeOnly: isIncomeOnlyAnnuity({ purchaseAmount: amount, annuityType: lever.annuityType, residualCapital: lever.residualCapital }),
        incomeAYear: Number(row?.annuityTerms?.annualPayment) || null,
    };
}

/** The name of the scenario a lever was layered on ("Pay 75% RAD, Keep Home (Bank -> Shares)"), or null. */
export function leverBaseName(name: string | null | undefined): string | null {
    const n = String(name || '');
    const i = n.indexOf(' + ');
    return i > 0 ? n.slice(0, i) : null;
}

/** The evaluated row the lever was layered on: the same deposit, paid the same way, without it. */
export function leverBaseRowOf<T extends EngineRowLike>(row: EngineRowLike | null | undefined, evaluated: T[] | null | undefined): T | null {
    const baseName = leverBaseName(row?.name);
    if (!baseName) return null;
    return (evaluated || []).find((r) => r?.name === baseName) ?? null;
}

/** The sentence under the table naming the comparison. */
export function leverCompareNote(amount: number): string {
    return `the same deposit, paid the same way, with the ${formatCurrency(Math.round(amount))} left in savings instead`;
}

/** The panel on a tested plan card (nothing when the plan buys no guaranteed income). */
export const EngineAnnuityFamilyPanel: React.FC<{
    row: EngineRowLike;
    evaluated: EngineRowLike[] | null | undefined;
    factFind: DraftFactFind | null | undefined;
    personName: string;
    horizonYears: number;
    testId?: string;
}> = ({ row, evaluated, factFind, personName, horizonYears, testId }) => {
    const lever = engineAnnuityLeverOf(row);
    if (!lever) return null;
    const base = leverBaseRowOf(row, evaluated);
    const compareRows = (base?.projections as never[] | undefined) || null;
    return (
        <AnnuityFamilyPanel
            factFind={factFind}
            personName={personName}
            amount={lever.amount}
            kind={lever.kind}
            incomeAYear={lever.incomeAYear}
            rows={(row.projections as never[] | undefined) || []}
            compareRows={compareRows}
            compareNote={compareRows?.length ? leverCompareNote(lever.amount) : null}
            horizonYears={horizonYears}
            testId={testId}
        />
    );
};
